import { DomainEditorWorkspace } from "@/components/domain-editor";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppShell } from "@/components/app-shell";
import { AuthShell } from "@/components/auth/auth-shell";
import { StepUpProvider } from "@/components/step-up-provider";
import { WorkspaceLoading } from "@/components/workspace/workspace-state";
import { AppErrorBoundary } from "./error-boundary";
import MediaPage from "@/app/(panel)/medya/page";

const LoginPage = lazy(() => import("@/app/(auth)/giris/page"));
const ForgotPasswordPage = lazy(
  () => import("@/app/(auth)/sifremi-unuttum/page"),
);
const ResetPasswordPage = lazy(
  () => import("@/app/(auth)/sifre-yenile/[uid]/[token]/page"),
);
const InvitationPage = lazy(() => import("@/app/(auth)/davet/[id]/page"));
const DashboardPage = lazy(() => import("@/app/(panel)/dashboard/page"));
const WorkflowPage = lazy(() => import("@/app/(panel)/icerik/page"));
const ServicesPage = lazy(() => import("@/app/(panel)/hizmetler/page"));
const FinancePage = lazy(() => import("@/app/(panel)/finansman/page"));
const ComparisonsPage = lazy(
  () => import("@/app/(panel)/karsilastirmalar/page"),
);
const EditorialPage = lazy(() => import("@/app/(panel)/editorial/page"));
const CorporatePage = lazy(() => import("@/app/(panel)/kurumsal/page"));
const MediaDetailPage = lazy(() => import("@/app/(panel)/medya/[id]/page"));
const SubmissionsPage = lazy(() => import("@/app/(panel)/basvurular/page"));
const SubmissionPage = lazy(() => import("@/app/(panel)/basvurular/[id]/page"));
const MembersPage = lazy(() => import("@/app/(panel)/uyeler/page"));
const MemberPage = lazy(() => import("@/app/(panel)/uyeler/[id]/page"));
const OpportunitiesPage = lazy(() => import("@/app/(panel)/firsatlar/page"));
const OpportunitySettingsPage = lazy(() => import("@/app/(panel)/firsat-ayarlari/page"));
const OpportunityRequestsPage = lazy(() => import("@/app/(panel)/firsat-talepleri/page"));
const OpportunityRequestPage = lazy(() => import("@/app/(panel)/firsat-talepleri/[id]/page"));
const OpportunityPage = lazy(() => import("@/app/(panel)/firsatlar/[id]/page"));
const NewsletterPage = lazy(() => import("@/app/(panel)/bulten/page"));
const UsersPage = lazy(() => import("@/app/(panel)/kullanicilar/page"));
const RolesPage = lazy(() => import("@/app/(panel)/roller/page"));
const UserDetailPage = lazy(
  () => import("@/app/(panel)/kullanicilar/[id]/page"),
);
const AuditPage = lazy(() => import("@/app/(panel)/denetim/page"));
const TrashPage = lazy(() => import("@/app/(panel)/cop-kutusu/page"));
const SettingsPage = lazy(() => import("@/app/(panel)/ayarlar/page"));
const NotificationsPage = lazy(() => import("@/app/(panel)/bildirimler/page"));
const HomePageEditor = lazy(() => import("@/app/(panel)/anasayfa/page"));
const HeroPageEditor = lazy(() => import("@/app/(panel)/hero/page"));
const HeroDetailEditor = lazy(() => import("@/app/(panel)/hero/[id]/page"));
const SitePages = lazy(() => import("@/app/(panel)/sayfalar/page"));
const ServiceEditor = lazy(() =>
  import("@/components/editors/service-editor").then((module) => ({
    default: module.ServiceEditor,
  })),
);
const FinanceEditor = lazy(() =>
  import("@/components/editors/finance-editor").then((module) => ({
    default: module.FinanceEditor,
  })),
);
const EditorialEditor = lazy(() =>
  import("@/components/editors/editorial-editor").then((module) => ({
    default: module.EditorialEditor,
  })),
);
const SiteContentEditorRoute = lazy(() =>
  import("@/components/site-content-manager").then((module) => ({
    default: module.SiteContentEditorRoute,
  })),
);
const CorporateEditor = lazy(() =>
  import("@/components/corporate-editor").then((module) => ({
    default: module.CorporateEditor,
  })),
);

function AuthLayout({ children }: { children: React.ReactNode }) {
  return <AuthShell>{children}</AuthShell>;
}

function PanelLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function EditorRoute({
  mode,
  fallback,
}: {
  mode: "service" | "finance" | "editorial";
  fallback: string;
}) {
  const { id } = useParams();
  if (!id) return <Navigate to={fallback} replace />;
  if (mode === "service") return <ServiceEditor id={id} returnTo={fallback} />;
  if (mode === "finance") return <FinanceEditor id={id} returnTo={fallback} />;
  return <EditorialEditor id={id} returnTo={fallback} />;
}

function ReferenceEditorRoute() {
  const { id } = useParams();
  return id ? <DomainEditorWorkspace mode="reference" id={id} returnTo="/referanslar" /> : <Navigate to="/referanslar" replace />;
}

function ReferenceCompanyEditorRoute() {
  const { id } = useParams();
  return id ? <DomainEditorWorkspace mode="referenceCompany" id={id} returnTo="/referanslar?kind=reference_company" /> : <Navigate to="/referanslar?kind=reference_company" replace />;
}

function CorporateEditorRoute() {
  const { kind, id } = useParams();
  if (kind === "reference" && id) return <Navigate to={`/referanslar/${id}`} replace />;
  return kind && id && ["team", "partner", "reference"].includes(kind) ? (
    <CorporateEditor kind={kind as "team" | "partner" | "reference"} id={id} />
  ) : (
    <Navigate to="/kurumsal" replace />
  );
}

export function AppRouter() {
  const { pathname } = useLocation();
  const isAuthRoute =
    pathname === "/giris" ||
    pathname === "/sifremi-unuttum" ||
    pathname.startsWith("/sifre-yenile/") ||
    pathname.startsWith("/davet/");
  const routes = (
    <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/giris"
          element={
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          }
        />
        <Route
          path="/sifremi-unuttum"
          element={
            <AuthLayout>
              <ForgotPasswordPage />
            </AuthLayout>
          }
        />
        <Route
          path="/sifre-yenile/:uid/:token"
          element={
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          }
        />
        <Route
          path="/davet/:id"
          element={
            <AuthLayout>
              <InvitationPage />
            </AuthLayout>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PanelLayout>
              <DashboardPage />
            </PanelLayout>
          }
        />
        <Route
          path="/icerik"
          element={
            <PanelLayout>
              <WorkflowPage />
            </PanelLayout>
          }
        />
        <Route
          path="/anasayfa"
          element={
            <PanelLayout>
              <HomePageEditor />
            </PanelLayout>
          }
        />
        <Route
          path="/hero"
          element={
            <PanelLayout>
              <HeroPageEditor />
            </PanelLayout>
          }
        />
        <Route
          path="/hero/:id"
          element={
            <PanelLayout>
              <HeroDetailEditor />
            </PanelLayout>
          }
        />
        <Route
          path="/sayfalar"
          element={
            <PanelLayout>
              <SitePages />
            </PanelLayout>
          }
        />
        <Route
          path="/sayfalar/:kind/:id"
          element={
            <PanelLayout>
              <SiteContentEditorRoute />
            </PanelLayout>
          }
        />
        <Route
          path="/hizmetler"
          element={
            <PanelLayout>
              <ServicesPage />
            </PanelLayout>
          }
        />
        <Route
          path="/hizmetler/:id"
          element={
            <PanelLayout>
              <EditorRoute mode="service" fallback="/hizmetler" />
            </PanelLayout>
          }
        />
        <Route
          path="/finansman"
          element={
            <PanelLayout>
              <FinancePage />
            </PanelLayout>
          }
        />
        <Route
          path="/finansman/:id"
          element={
            <PanelLayout>
              <EditorRoute mode="finance" fallback="/finansman" />
            </PanelLayout>
          }
        />
        <Route
          path="/karsilastirmalar"
          element={
            <PanelLayout>
              <ComparisonsPage />
            </PanelLayout>
          }
        />
        <Route
          path="/blog"
          element={
            <PanelLayout>
              <EditorialPage scope="blog" />
            </PanelLayout>
          }
        />
        <Route
          path="/blog/:id"
          element={
            <PanelLayout>
              <EditorRoute mode="editorial" fallback="/blog" />
            </PanelLayout>
          }
        />
        <Route
          path="/egitim"
          element={
            <PanelLayout>
              <EditorialPage scope="training" />
            </PanelLayout>
          }
        />
        <Route
          path="/egitim/:id"
          element={
            <PanelLayout>
              <EditorRoute mode="editorial" fallback="/egitim" />
            </PanelLayout>
          }
        />
        <Route
          path="/editorial"
          element={
            <PanelLayout>
              <EditorialPage />
            </PanelLayout>
          }
        />
        <Route
          path="/editorial/:id"
          element={
            <PanelLayout>
              <EditorRoute mode="editorial" fallback="/editorial" />
            </PanelLayout>
          }
        />
        <Route path="/referanslar" element={<PanelLayout><CorporatePage referenceOnly /></PanelLayout>} />
        <Route path="/referanslar/sirketler/:id" element={<PanelLayout><ReferenceCompanyEditorRoute /></PanelLayout>} />
        <Route path="/referanslar/:id" element={<PanelLayout><ReferenceEditorRoute /></PanelLayout>} />
        <Route
          path="/kurumsal"
          element={
            <PanelLayout>
              <CorporatePage />
            </PanelLayout>
          }
        />
        <Route
          path="/kurumsal/:kind/:id"
          element={
            <PanelLayout>
              <CorporateEditorRoute />
            </PanelLayout>
          }
        />
        <Route
          path="/medya"
          element={
            <PanelLayout>
              <MediaPage />
            </PanelLayout>
          }
        />
        <Route
          path="/medya/:id"
          element={
            <PanelLayout>
              <MediaDetailPage />
            </PanelLayout>
          }
        />
        <Route
          path="/basvurular"
          element={
            <PanelLayout>
              <SubmissionsPage />
            </PanelLayout>
          }
        />
        <Route
          path="/basvurular/:id"
          element={
            <PanelLayout>
              <SubmissionPage />
            </PanelLayout>
          }
        />
        <Route
          path="/uyeler"
          element={
            <PanelLayout>
              <MembersPage />
            </PanelLayout>
          }
        />
        <Route
          path="/uyeler/:id"
          element={
            <PanelLayout>
              <MemberPage />
            </PanelLayout>
          }
        />
        <Route
          path="/firsatlar"
          element={
            <PanelLayout>
              <OpportunitiesPage />
            </PanelLayout>
          }
        />
        <Route
          path="/firsat-ayarlari" element={<PanelLayout><OpportunitySettingsPage /></PanelLayout>}
        />
        <Route path="/firsat-talepleri" element={<PanelLayout><OpportunityRequestsPage /></PanelLayout>} />
        <Route path="/firsat-talepleri/:id" element={<PanelLayout><OpportunityRequestPage /></PanelLayout>} />
        <Route
          path="/firsatlar/:id"
          element={
            <PanelLayout>
              <OpportunityPage />
            </PanelLayout>
          }
        />
        <Route
          path="/bulten"
          element={
            <PanelLayout>
              <NewsletterPage />
            </PanelLayout>
          }
        />
        <Route
          path="/kullanicilar"
          element={
            <PanelLayout>
              <UsersPage />
            </PanelLayout>
          }
        />
        <Route
          path="/kullanicilar/:id"
          element={
            <PanelLayout>
              <UserDetailPage />
            </PanelLayout>
          }
        />
        <Route
          path="/roller"
          element={
            <PanelLayout>
              <RolesPage />
            </PanelLayout>
          }
        />
        <Route
          path="/denetim"
          element={
            <PanelLayout>
              <AuditPage />
            </PanelLayout>
          }
        />
        <Route
          path="/cop-kutusu"
          element={<PanelLayout><TrashPage /></PanelLayout>}
        />
        <Route
          path="/ayarlar"
          element={
            <PanelLayout>
              <SettingsPage />
            </PanelLayout>
          }
        />
        <Route
          path="/bildirimler"
          element={
            <PanelLayout>
              <NotificationsPage />
            </PanelLayout>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );

  if (isAuthRoute) {
    return (
      <AppErrorBoundary key={pathname}>
        <Suspense fallback={<WorkspaceLoading label="Sayfa yükleniyor" lines={6} />}>
          {routes}
        </Suspense>
      </AppErrorBoundary>
    );
  }

  return (
    <AppShell>
      <StepUpProvider>
        <AppErrorBoundary key={pathname}>
          <Suspense fallback={<WorkspaceLoading label="Sayfa yükleniyor" lines={6} />}>
            {routes}
          </Suspense>
        </AppErrorBoundary>
      </StepUpProvider>
    </AppShell>
  );
}
